import { useState } from "react";

import { useToast } from "@/ds";
import type { FichaAnexo, IncluirFichaAnexoPayload } from "@/modules/financeiro-fichas";
import { useMediaQuery } from "@/hooks/use-media-query";
import { HUB_PERMISSOES, usePermissoes } from "@/modules/permissoes";
import { formatarTamanhoAnexo } from "@/shared/components";
import {
  ANEXO_ARQUIVO_ERROR_MESSAGE,
  ANEXO_INCLUIDO_MESSAGE,
  ANEXO_INCLUIR_ERROR_MESSAGE,
  ANEXO_REMOVER_ERROR_MESSAGE,
  ANEXO_REMOVIDO_MESSAGE,
  useAnexosFicha,
} from "../../hooks/use-anexos-ficha";
import { MOBILE_QUERY } from "../../../financeiro-obras.constants";
import { FICHAS_FORBIDDEN_MESSAGE } from "../../hooks/use-fichas-financeiras";
import { ANEXO_TOAST_ERRO_LIFE, ANEXO_TOAST_SUCESSO_LIFE } from "./ficha-anexos.constants";
import type { FichaAnexoLinha, FichaAnexosProps } from "./types-ficha-anexos";

/**
 * Seção Anexos da ficha (HU 2688): relação, inclusão (janela própria) e remoção (com confirmação),
 * baixar e ver o arquivo. Incluir e remover exigem `hub.financeiro:add-ficha`; sem ele as ações somem.
 */
export function useFichaAnexos({ obraId, fichaId, title }: FichaAnexosProps) {
  const { status: permissoesStatus, hasPermissao } = usePermissoes();
  const semConsulta =
    permissoesStatus === "success" && !hasPermissao(HUB_PERMISSOES.FINANCEIRO_FICHAS_CONSULTA);
  const model = useAnexosFicha(obraId, fichaId, !semConsulta);
  const podeEditar = hasPermissao(HUB_PERMISSOES.FINANCEIRO_FICHAS_GERACAO);
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const toast = useToast();
  const [incluindo, setIncluindo] = useState(false);
  const [removendo, setRemovendo] = useState<FichaAnexo | null>(null);

  const linhas: FichaAnexoLinha[] = model.anexos.map((anexo) => ({
    id: anexo.id,
    identificacao: anexo.identificacao || anexo.nomeArquivo,
    formato: anexo.formato.toUpperCase(),
    tamanho: formatarTamanhoAnexo(anexo.tamanhoBytes),
    testIds: {
      linha: `ficha-anexo-${anexo.id}`,
      ver: `ficha-anexo-ver-${anexo.id}`,
      baixar: `ficha-anexo-baixar-${anexo.id}`,
      remover: `ficha-anexo-remover-${anexo.id}`,
    },
  }));

  function avisar(severity: "success" | "error", summary: string) {
    toast.show({
      severity,
      summary,
      life: severity === "success" ? ANEXO_TOAST_SUCESSO_LIFE : ANEXO_TOAST_ERRO_LIFE,
    });
  }

  async function salvarInclusao(payload: IncluirFichaAnexoPayload): Promise<boolean> {
    const resultado = await model.incluir([payload]);

    if (resultado === "erro") {
      avisar("error", ANEXO_INCLUIR_ERROR_MESSAGE);
      return false;
    }
    setIncluindo(false);
    avisar("success", ANEXO_INCLUIDO_MESSAGE);
    return true;
  }

  async function confirmarRemocao() {
    if (!removendo) return;

    // Rejeitar mantém a confirmação aberta com a mensagem dentro dela.
    if ((await model.remover(removendo.id)) === "erro") {
      throw new Error(ANEXO_REMOVER_ERROR_MESSAGE);
    }
    setRemovendo(null);
    avisar("success", ANEXO_REMOVIDO_MESSAGE);
  }

  async function usarArquivo(
    id: string,
    acao: (anexo: FichaAnexo) => Promise<"ok" | "erro">,
  ): Promise<void> {
    const anexo = model.anexos.find((item) => item.id === id);
    if (anexo && (await acao(anexo)) === "erro") avisar("error", ANEXO_ARQUIVO_ERROR_MESSAGE);
  }

  return {
    isMobile,
    title,
    // Sem a 020 nem consulta: mostra só "Acesso não autorizado", sem botão nem lista.
    status: semConsulta ? ("forbidden" as const) : model.status,
    errorMessage: semConsulta ? FICHAS_FORBIDDEN_MESSAGE : model.errorMessage,
    recarregar: model.reload,
    linhas,
    incluindo,
    removendoIdentificacao: removendo ? removendo.identificacao || removendo.nomeArquivo : null,
    podeEditar,
    abrirInclusao: () => setIncluindo(true),
    cancelarInclusao: () => setIncluindo(false),
    salvarInclusao,
    pedirRemocao: (id: string) =>
      setRemovendo(model.anexos.find((anexo) => anexo.id === id) ?? null),
    cancelarRemocao: () => setRemovendo(null),
    confirmarRemocao,
    baixarArquivo: (id: string) => usarArquivo(id, model.baixar),
    verArquivo: (id: string) => usarArquivo(id, model.abrir),
  };
}
